import React from 'react';
import { formatCurrency, formatDuration } from '../../utils/format';
import LoadingSpinner from '../UI/LoadingSpinner';

const FeeDisplay = ({ feeEstimate, isLoading, timeDelay }) => {
  if (isLoading) {
    return (
      <div className="bg-slate-50 dark:bg-slate-800/50 rounded-lg p-4">
        <div className="flex items-center gap-2 mb-3">
          <LoadingSpinner size="sm" />
          <span className="text-sm font-medium">Estimating fees...</span>
        </div>
      </div>
    );
  }

  if (!feeEstimate) return null;

  return (
    <div className="bg-slate-50 dark:bg-slate-800/50 rounded-lg p-4 space-y-3">
      <h4 className="font-medium text-slate-900 dark:text-slate-100">
        Transaction Fees
      </h4>
      
      <div className="space-y-2 text-sm">
        {/* Network Fee */}
        <div className="flex justify-between">
          <span className="text-slate-600 dark:text-slate-400">Network Fee:</span>
          <span className="font-medium">
            {formatCurrency(feeEstimate.networkFeeUsd || feeEstimate.totalFeeUsd)} 
            <span className="text-slate-500 ml-1">
              ({feeEstimate.networkFeeEth || feeEstimate.totalFeeEth} Native)
            </span>
          </span>
        </div>

        {/* Gas Fee */}
        {feeEstimate.gasFeeUsd && parseFloat(feeEstimate.gasFeeUsd) > 0 && (
          <div className="flex justify-between">
            <span className="text-slate-600 dark:text-slate-400">Gas Fee:</span>
            <span className="font-medium">
              {formatCurrency(feeEstimate.gasFeeUsd)}
              <span className="text-slate-500 ml-1">
                ({feeEstimate.gasFeeEth} Native)
              </span>
            </span>
          </div>
        )}

        {/* Total Fee */}
        <div className="border-t border-slate-200 dark:border-slate-700 pt-2">
          <div className="flex justify-between font-medium">
            <span>Total Fee:</span>
            <span className="text-primary-600 dark:text-primary-400">
              {formatCurrency(feeEstimate.totalFeeUsd)}
              <span className="text-slate-500 ml-1">
                ({feeEstimate.totalFeeEth} Native)
              </span>
            </span>
          </div>
        </div>

        {/* Processing Time */}
        {timeDelay && (
          <div className="flex justify-between">
            <span className="text-slate-600 dark:text-slate-400">Processing Time:</span>
            <span className="font-medium">
              {formatDuration(timeDelay.delaySeconds)}
            </span>
          </div>
        )}

        {/* Fee Breakdown Details */}
        {feeEstimate.breakdown && (
          <div className="mt-3 pt-3 border-t border-slate-200 dark:border-slate-700">
            <div className="text-xs text-slate-500 dark:text-slate-400 space-y-1">
              <div className="flex justify-between">
                <span>Base Fee:</span>
                <span>{formatCurrency(feeEstimate.breakdown.baseFee)}</span>
              </div>
              {feeEstimate.breakdown.percentageFee && parseFloat(feeEstimate.breakdown.percentageFee) > 0 && (
                <div className="flex justify-between">
                  <span>Percentage Fee:</span>
                  <span>{formatCurrency(feeEstimate.breakdown.percentageFee)}</span>
                </div>
              )}
              {feeEstimate.breakdown.relayerFee && parseFloat(feeEstimate.breakdown.relayerFee) > 0 && (
                <div className="flex justify-between">
                  <span>Relayer Fee:</span>
                  <span>{formatCurrency(feeEstimate.breakdown.relayerFee)}</span>
                </div>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Fee Information */}
      <div className="mt-3 p-3 bg-blue-50 dark:bg-blue-900/20 rounded-lg">
        <p className="text-xs text-blue-700 dark:text-blue-300">
          💡 Fees are paid in the native currency of the source chain.
          {timeDelay && timeDelay.delaySeconds > 0 && (
            <span className="block mt-1">
              ⏱️ Large amounts require additional security delays.
            </span>
          )}
        </p>
      </div>
    </div>
  );
};

export default FeeDisplay; 